import { defineStore } from 'pinia';
import { Image } from 'itk-wasm';
import * as DICOM from '@/src/io/dicom';
import { Chunk } from '@/src/core/streaming/chunk';
import { useImageCacheStore } from '@/src/store/image-cache';
import DicomChunkImage from '@/src/core/streaming/dicomChunkImage';
import { Tags } from '@/src/core/dicomTags';
import { removeFromArray } from '../utils';
import {
  DICOM_FORCE_2D_STACK_ID_KEY,
  DICOM_FORCE_2D_STACK_INDEX_KEY,
  DICOM_FORCE_2D_STACK_KEY,
} from '@/src/core/streaming/dicomDisplayOptions';

export const ANONYMOUS_PATIENT = 'Anonymous';
export const ANONYMOUS_PATIENT_ID = 'ANONYMOUS';

export function imageCacheMultiKey(offset: number, asThumbnail: boolean) {
  return `${offset}!!${asThumbnail}`;
}

export interface VolumeKeys {
  patientKey: string;
  studyKey: string;
  volumeKey: string;
}

export interface PatientInfo {
  PatientID: string;
  PatientName: string;
  PatientBirthDate: string;
  PatientSex: string;
}

export interface StudyInfo {
  StudyID: string;
  StudyInstanceUID: string;
  StudyDate: string;
  StudyTime: string;
  AccessionNumber: string;
  StudyDescription: string;
}

export interface VolumeInfo {
  NumberOfSlices: number;
  VolumeID: string;
  Modality: string;
  SeriesInstanceUID: string;
  SeriesNumber: string;
  SeriesDescription: string;
  WindowLevel: string;
  WindowWidth: string;
}

export interface OtherInfo {
  HospitalName?: string;
  PatientAge?: string;
  Part?: string;
  RepetitionTime?: string;
  EchoTime?: string;
  InversionTime?: string;
  FlipAngle?: string;
  ScanningSequence?: string;
  SequenceVariant?: string;
  ScanOptions?: string;
  MRAcquisitionType?: string;
  SequenceName?: string;
  AngioFlag?: string;
  SliceThickness?: string;
  SpacingBetweenSlices?: string;
  FieldOfView?: string;
  PercentSampling?: string;
  PercentPhaseFieldOfView?: string;
  EchoNumbers?: string;
  NumberOfEchoes?: string;
  EchoTrainLength?: string;
  InstanceNumber?: string;
  SliceLocation?: string;
  PatientPosition?: string;
  Manufacturer?: string;
  ManufacturerModelName?: string;
  DeviceSerialNumber?: string;
  SoftwareVersions?: string;
  AcquisitionNumber?: string;
  AcquisitionDate?: string;
  AcquisitionTime?: string;
  AcquisitionDateTime?: string;
  SeriesDate?: string;
  SeriesTime?: string;
  ContentDate?: string;
  ContentTime?: string;
  PatientName?: string;
  AccessionNumber?: string;
  PatientSex?: string;
  KVP?: string;
  XRayTubeCurrent?: string;
  Modality?: string;
  ConvolutionKernel?: string;
  Exposure?: string;
}

interface State {
  // volumeKey -> imageCacheMultiKey -> ITKImage
  sliceData: Record<string, Record<string, Image>>;

  // volume invalidation information
  needsRebuild: Record<string, boolean>;

  // patientKey -> patient info
  patientInfo: Record<string, PatientInfo>;
  // patientKey -> array of studyKeys
  patientStudies: Record<string, string[]>;

  // studyKey -> study info
  studyInfo: Record<string, StudyInfo>;
  // studyKey -> array of volumeKeys
  studyVolumes: Record<string, string[]>;

  // volumeKey -> volume info
  volumeInfo: Record<string, VolumeInfo>;
  // volumeKey -> additional dicom metadata
  otherInfo: Record<string, OtherInfo>;

  // parent pointers
  // volumeKey -> studyKey
  volumeStudy: Record<string, string>;
  // studyKey -> patientKey
  studyPatient: Record<string, string>;
}

/**
 * Trims and collapses multiple spaces into one.
 * @param name
 * @returns string
 */
const cleanupName = (name: string) => {
  return name.trim().replace(/\s+/g, ' ');
};

export const getDisplayName = (info: VolumeInfo) => {
  return (
    cleanupName(info.SeriesDescription || info.SeriesNumber) ||
    info.SeriesInstanceUID
  );
};

export const getWindowLevels = (info: VolumeInfo) => {
  const { WindowWidth, WindowLevel } = info;
  if (
    WindowWidth == null ||
    WindowLevel == null ||
    WindowWidth === '' ||
    WindowLevel === ''
  )
    return []; // missing tag
  const widths = WindowWidth.split('\\').map(parseFloat);
  const levels = WindowLevel.split('\\').map(parseFloat);
  if (
    widths.some((w) => Number.isNaN(w)) ||
    levels.some((l) => Number.isNaN(l))
  ) {
    console.error('Invalid WindowWidth or WindowLevel DICOM tags');
    return [];
  }
  if (widths.length !== levels.length) {
    console.error(
      'Different numbers of WindowWidth and WindowLevel DICOM tags'
    );
    return [];
  }
  return widths.map((width, i) => ({ width, level: levels[i] }));
};

function isForce2DStackChunk(chunk: Chunk) {
  return chunk.getUserData(DICOM_FORCE_2D_STACK_KEY) === true;
}

function getForce2DStackId(chunk: Chunk) {
  const stackId = chunk.getUserData(DICOM_FORCE_2D_STACK_ID_KEY);
  return typeof stackId === 'string' ? stackId : 'force-2d-stack';
}

function getForce2DStackIndex(chunk: Chunk) {
  const stackIndex = chunk.getUserData(DICOM_FORCE_2D_STACK_INDEX_KEY);
  return typeof stackIndex === 'number' ? stackIndex : Number.MAX_SAFE_INTEGER;
}

function getForce2DVolumeId(stackId: string, chunks: Chunk[]) {
  const metadata = Object.fromEntries(chunks[0].metadata ?? []);
  const seriesInstanceUID = metadata[Tags.SeriesInstanceUID] || stackId;
  return `${seriesInstanceUID}.2D.${stackId}`;
}

export const useDICOMStore = defineStore('dicom', {
  state: (): State => ({
    sliceData: {},
    patientInfo: {},
    patientStudies: {},
    studyInfo: {},
    studyVolumes: {},
    volumeInfo: {},
    otherInfo: {},
    volumeStudy: {},
    studyPatient: {},
    needsRebuild: {},
  }),
  actions: {
    async importChunks(chunks: Chunk[]) {
      const imageCacheStore = useImageCacheStore();
      const force2DChunks = chunks.filter(isForce2DStackChunk);
      const volumeChunks = chunks.filter(
        (chunk) => !isForce2DStackChunk(chunk)
      );

      // split into groups
      const chunksByVolume =
        volumeChunks.length > 0
          ? await DICOM.splitAndSort(volumeChunks, (chunk) => chunk.metaBlob!)
          : {};

      const force2DGroups = force2DChunks.reduce<Record<string, Chunk[]>>(
        (groups, chunk) => {
          const stackId = getForce2DStackId(chunk);
          groups[stackId] ??= [];
          groups[stackId].push(chunk);
          return groups;
        },
        {}
      );

      Object.entries(force2DGroups).forEach(([stackId, groupChunks]) => {
        const sortedChunks = [...groupChunks].sort(
          (a, b) => getForce2DStackIndex(a) - getForce2DStackIndex(b)
        );
        chunksByVolume[getForce2DVolumeId(stackId, sortedChunks)] =
          sortedChunks;
      });

      await Promise.all(
        Object.entries(chunksByVolume).map(async ([id, sortedChunks]) => {
          const image = imageCacheStore.imageById[id] ?? new DicomChunkImage();
          if (!(image instanceof DicomChunkImage)) {
            throw new Error('image is not a DicomChunkImage');
          }

          await image.addChunks(sortedChunks);
          imageCacheStore.addProgressiveImage(image, { id });

          // update database
          const metaPairs = image.getDicomMetadata();

          // console.log('metaPairs', metaPairs);

          // console.log('metaPairs', metaPairs);
          if (!metaPairs) throw new Error('Metadata not ready');
          const metadata = Object.fromEntries(metaPairs);

          const patientInfo: PatientInfo = {
            PatientID: metadata[Tags.PatientID],
            PatientName: metadata[Tags.PatientName],
            PatientBirthDate: metadata[Tags.PatientBirthDate],
            PatientSex: metadata[Tags.PatientSex],
          };

          const studyInfo: StudyInfo = {
            StudyID: metadata[Tags.StudyID],
            StudyInstanceUID: metadata[Tags.StudyInstanceUID],
            StudyDate: metadata[Tags.StudyDate],
            StudyTime: metadata[Tags.StudyTime],
            AccessionNumber: metadata[Tags.AccessionNumber],
            StudyDescription: metadata[Tags.StudyDescription],
          };

          const volumeInfo: VolumeInfo = {
            NumberOfSlices: image.getChunks().length,
            VolumeID: id,
            Modality: metadata[Tags.Modality],
            SeriesInstanceUID: metadata[Tags.SeriesInstanceUID],
            SeriesNumber: metadata[Tags.SeriesNumber],
            SeriesDescription: metadata[Tags.SeriesDescription],
            WindowLevel: metadata[Tags.WindowLevel],
            WindowWidth: metadata[Tags.WindowWidth],
          };

          const otherInfo: OtherInfo = {
            HospitalName: metadata[Tags.InstitutionName],
            PatientAge: parseFloat(metadata[Tags.PatientAge]) + '岁',
            Part: metadata[Tags.Part],
            RepetitionTime: metadata[Tags.RepetitionTime],
            EchoTime: metadata[Tags.EchoTime],
            InversionTime: metadata[Tags.InversionTime],
            FlipAngle: metadata[Tags.FlipAngle],
            ScanningSequence: metadata[Tags.ScanningSequence],
            SequenceVariant: metadata[Tags.SequenceVariant],
            ScanOptions: metadata[Tags.ScanOptions],
            MRAcquisitionType: metadata[Tags.MRAcquisitionType],
            SequenceName: metadata[Tags.SequenceName],
            AngioFlag: metadata[Tags.AngioFlag],
            SliceThickness: metadata[Tags.SliceThickness],
            SpacingBetweenSlices: metadata[Tags.SpacingBetweenSlices]
              ? parseFloat(metadata[Tags.SpacingBetweenSlices]).toFixed(1)
              : '',
            FieldOfView: metadata[Tags.FieldOfView],
            PercentSampling: metadata[Tags.PercentSampling],
            PercentPhaseFieldOfView: metadata[Tags.PercentPhaseFieldOfView],
            EchoNumbers: metadata[Tags.EchoNumbers],
            NumberOfEchoes: metadata[Tags.NumberOfEchoes],
            EchoTrainLength: metadata[Tags.EchoTrainLength],
            InstanceNumber: metadata[Tags.InstanceNumber],
            SliceLocation: metadata[Tags.SliceLocation],
            PatientPosition: metadata[Tags.PatientPosition],
            Manufacturer: metadata[Tags.Manufacturer],
            ManufacturerModelName: metadata[Tags.ManufacturerModelName],
            DeviceSerialNumber: metadata[Tags.DeviceSerialNumber],
            SoftwareVersions: metadata[Tags.SoftwareVersions],
            AcquisitionNumber: metadata[Tags.AcquisitionNumber],
            AcquisitionDate: metadata[Tags.AcquisitionDate],
            AcquisitionTime: metadata[Tags.AcquisitionTime],
            AcquisitionDateTime: metadata[Tags.AcquisitionDateTime],
            SeriesDate: metadata[Tags.SeriesDate],
            SeriesTime: metadata[Tags.SeriesTime],
            ContentDate: metadata[Tags.ContentDate],
            ContentTime: metadata[Tags.ContentTime],
            PatientName: metadata[Tags.PatientName],
            AccessionNumber: metadata[Tags.AccessionNumber],
            PatientSex: metadata[Tags.PatientSex].trim() === 'M' ? '男' : '女',
            KVP: metadata[Tags.KVP] ? metadata[Tags.KVP].trim() + 'KV' : '',
            XRayTubeCurrent: metadata[Tags.XRayTubeCurrent]
              ? metadata[Tags.XRayTubeCurrent].trim() + 'mA'
              : '',
            Exposure: metadata[Tags.Exposure]
              ? metadata[Tags.Exposure].trim() + 'mAs'
              : '',
          };

          // console.log(otherInfo, 'otherInfo');
          // console.log(volumeInfo, 'volumeInfo');
          // console.log(studyInfo, 'studyInfo');

          this._updateDatabase(patientInfo, studyInfo, volumeInfo, otherInfo);

          // save the image name
          image.setName(getDisplayName(volumeInfo));
        })
      );

      return chunksByVolume;
    },

    _updateDatabase(
      patient: PatientInfo,
      study: StudyInfo,
      volume: VolumeInfo,
      other: OtherInfo
    ) {
      const patientKey = patient.PatientID;
      const studyKey = study.StudyInstanceUID;
      const volumeKey = volume.VolumeID;

      if (!(patientKey in this.patientInfo)) {
        this.patientInfo[patientKey] = patient;
        this.patientStudies[patientKey] = [];
      }

      if (!(studyKey in this.studyInfo)) {
        this.studyInfo[studyKey] = study;
        this.studyVolumes[studyKey] = [];
        this.studyPatient[studyKey] = patientKey;
        this.patientStudies[patientKey].push(studyKey);
      }

      if (!(volumeKey in this.volumeInfo)) {
        this.volumeInfo[volumeKey] = volume;
        this.otherInfo[volumeKey] = other;
        this.volumeStudy[volumeKey] = studyKey;
        this.sliceData[volumeKey] = {};
        this.studyVolumes[studyKey].push(volumeKey);
      } else {
        this.otherInfo[volumeKey] = other;
      }
    },

    // You should probably call datasetStore.remove instead as this does not
    // remove files/images/layers associated with the volume
    deleteVolume(volumeKey: string) {
      if (volumeKey in this.volumeInfo) {
        const studyKey = this.volumeStudy[volumeKey];
        delete this.volumeInfo[volumeKey];
        delete this.otherInfo[volumeKey];
        delete this.sliceData[volumeKey];
        delete this.volumeStudy[volumeKey];

        removeFromArray(this.studyVolumes[studyKey], volumeKey);
        if (this.studyVolumes[studyKey].length === 0) {
          this._deleteStudy(studyKey);
        }
      }
    },

    _deleteStudy(studyKey: string) {
      if (studyKey in this.studyInfo) {
        const patientKey = this.studyPatient[studyKey];
        delete this.studyInfo[studyKey];
        delete this.studyPatient[studyKey];

        [...this.studyVolumes[studyKey]].forEach((volumeKey) =>
          this.deleteVolume(volumeKey)
        );
        delete this.studyVolumes[studyKey];

        removeFromArray(this.patientStudies[patientKey], studyKey);
        if (this.patientStudies[patientKey].length === 0) {
          this._deletePatient(patientKey);
        }
      }
    },

    _deletePatient(patientKey: string) {
      if (patientKey in this.patientInfo) {
        delete this.patientInfo[patientKey];

        [...this.patientStudies[patientKey]].forEach((studyKey) =>
          this._deleteStudy(studyKey)
        );
        delete this.patientStudies[patientKey];
      }
    },
  },
});
